import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithAuth } from './baseQuery';

// Create the asset API slice
export const assetApi = createApi({
  reducerPath: 'assetApi',
  baseQuery: createBaseQueryWithAuth(),
  tagTypes: ['Asset', 'AssetRequest', 'AssetHistory'],

  endpoints: (builder) => ({
    // ========== ASSET ENDPOINTS ========== //

    // Get all assets
    getAssets: builder.query({
      query: (params = {}) => ({
        url: '/assets',
        params
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'Asset', id: _id })),
              { type: 'Asset', id: 'LIST' }
            ]
          : [{ type: 'Asset', id: 'LIST' }]
    }),

    // Get single asset
    getAsset: builder.query({
      query: (id) => `/assets/${id}`,
      providesTags: (result, error, id) => [{ type: 'Asset', id }]
    }),

    // Create asset
    createAsset: builder.mutation({
      query: (assetData) => ({
        url: '/assets',
        method: 'POST',
        body: assetData
      }),
      invalidatesTags: [{ type: 'Asset', id: 'LIST' }]
    }),

    // Update asset
    updateAsset: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/assets/${id}`,
        method: 'PUT',
        body: formData
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Delete asset
    deleteAsset: builder.mutation({
      query: (id) => ({
        url: `/assets/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: 'Asset', id: 'LIST' }]
    }),

    // Assign asset
    assignAsset: builder.mutation({
      query: ({ id, employeeId, condition, notes }) => ({
        url: `/assets/${id}/assign`,
        method: 'PATCH',
        body: { employeeId, condition, notes }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Return asset
    returnAsset: builder.mutation({
      query: ({ id, condition }) => ({
        url: `/assets/${id}/return`,
        method: 'PATCH',
        body: { condition }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Transfer asset
    transferAsset: builder.mutation({
      query: ({ id, toEmployeeId, condition, reason, notes }) => ({
        url: `/assets/${id}/transfer`,
        method: 'PATCH',
        body: { toEmployeeId, condition, reason, notes }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Get asset history
    getAssetHistory: builder.query({
      query: (id) => `/assets/${id}/history`,
      providesTags: (result, error, id) => [{ type: 'AssetHistory', id }]
    }),

    // Schedule maintenance
    scheduleMaintenance: builder.mutation({
      query: ({ id, type, description, startDate, cost, vendor, technician }) => ({
        url: `/assets/${id}/maintenance/schedule`,
        method: 'PATCH',
        body: { type, description, startDate, cost, vendor, technician }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Start maintenance
    startMaintenance: builder.mutation({
      query: ({ id, maintenanceIndex }) => ({
        url: `/assets/${id}/maintenance/start`,
        method: 'PATCH',
        body: { maintenanceIndex }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Complete maintenance
    completeMaintenance: builder.mutation({
      query: ({ id, maintenanceIndex, cost, technician, vendor, notes }) => ({
        url: `/assets/${id}/maintenance/complete`,
        method: 'PATCH',
        body: { maintenanceIndex, cost, technician, vendor, notes }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // Cancel maintenance
    cancelMaintenance: builder.mutation({
      query: ({ id, maintenanceIndex }) => ({
        url: `/assets/${id}/maintenance/cancel`,
        method: 'PATCH',
        body: { maintenanceIndex }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Asset', id },
        { type: 'Asset', id: 'LIST' }
      ]
    }),

    // ========== ASSET REQUEST ENDPOINTS ========== //

    // Get asset requests
    getAssetRequests: builder.query({
      query: (params = {}) => ({
        url: '/assets/request',
        params
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ _id }) => ({ type: 'AssetRequest', id: _id })),
              { type: 'AssetRequest', id: 'LIST' }
            ]
          : [{ type: 'AssetRequest', id: 'LIST' }]
    }),

    // Create asset request
    createAssetRequest: builder.mutation({
      query: (requestData) => ({
        url: '/assets/request',
        method: 'POST',
        body: requestData
      }),
      invalidatesTags: [{ type: 'AssetRequest', id: 'LIST' }]
    }),

    // Approve asset request
    approveAssetRequest: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/assets/request/${id}/approve`,
        method: 'PATCH',
        body: { remarks }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AssetRequest', id },
        { type: 'AssetRequest', id: 'LIST' }
      ]
    }),

    // Reject asset request
    rejectAssetRequest: builder.mutation({
      query: ({ id, remarks }) => ({
        url: `/assets/request/${id}/reject`,
        method: 'PATCH',
        body: { remarks }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AssetRequest', id },
        { type: 'AssetRequest', id: 'LIST' }
      ]
    }),

    // Fulfill asset request
    fulfillAssetRequest: builder.mutation({
      query: ({ id, assetId, condition }) => ({
        url: `/assets/request/${id}/fulfill`,
        method: 'PATCH',
        body: { assetId, condition }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AssetRequest', id },
        { type: 'AssetRequest', id: 'LIST' },
        { type: 'Asset', id: 'LIST' }
      ]
    })
  })
});

// Export hooks for usage in components
export const {
  // Asset hooks
  useGetAssetsQuery,
  useGetAssetQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useAssignAssetMutation,
  useReturnAssetMutation,
  useTransferAssetMutation,
  useGetAssetHistoryQuery,

  // Asset maintenance hooks
  useScheduleMaintenanceMutation,
  useStartMaintenanceMutation,
  useCompleteMaintenanceMutation,
  useCancelMaintenanceMutation,

  // Asset request hooks
  useGetAssetRequestsQuery,
  useCreateAssetRequestMutation,
  useApproveAssetRequestMutation,
  useRejectAssetRequestMutation,
  useFulfillAssetRequestMutation
} = assetApi;
